import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { httpLink, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '@web-app/server/routers/_app';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import {
  supportFailure,
  supportOperation,
  supportResult,
  traceIdSchema,
} from './telemetry';

const viewerSchema = z.object({
  viewerId: z.string().regex(/^user_[a-zA-Z0-9]+$/),
});

function requestId(meta?: Record<string, unknown>) {
  const response = meta?.response as Response | undefined;
  return traceIdSchema.safeParse(response?.headers.get('X-Request-ID')).data;
}

// Used only on the unbatched Support Concierge transport. Reads are counted on
// the server; browser logs cover write attempts and failures before a response.
const supportConciergeLoggingLink: TRPCLink<AppRouter> =
  () =>
  ({ op, next }) =>
    observable((observer) => {
      const operation = supportOperation(op.path);
      if (!operation) return next(op).subscribe(observer);
      const correlationId =
        traceIdSchema.safeParse(op.context.supportCorrelationId).data ?? uuid();
      const startedAt = Date.now();
      const writing = operation === 'create' || operation === 'reply';
      const context = logContext('support-concierge', operation, {
        schemaVersion: 1,
        layer: 'browser',
        eventType: 'api_request',
        stage: 'client-request',
        correlationId,
        clerkUserId: viewerSchema.safeParse(op.input).data?.viewerId,
      });
      if (writing)
        datadogLogs.logger.info('Support request started', {
          ...context,
          outcome: 'started',
          detail:
            'Provider submitted a support message; delivery is not yet confirmed',
        });
      return next({
        ...op,
        context: { ...op.context, supportCorrelationId: correlationId },
      }).subscribe({
        next(value) {
          if (writing && 'data' in value.result) {
            const summary = supportResult(operation, value.result.data);
            datadogLogs.logger[
              summary.outcome === 'failed'
                ? 'error'
                : summary.result === 'partial'
                  ? 'warn'
                  : 'info'
            ]('Support request received', {
              ...context,
              ...summary,
              requestId: requestId(value.context),
              durationMs: Date.now() - startedAt,
              detail:
                'Browser received the support delivery result; partial setup needs follow-up',
            });
          }
          observer.next(value);
        },
        error(error) {
          if (!op.signal?.aborted || writing)
            datadogLogs.logger.error('Support request failed in browser', {
              ...context,
              ...supportFailure(operation, error.data?.code),
              requestId: requestId(error.meta),
              durationMs: Date.now() - startedAt,
              detail:
                'Portal request failed; a submitted message may require delivery verification',
            });
          observer.error(error);
        },
        complete() {
          observer.complete();
        },
      });
    });

export function supportConciergeLinks(url: string): TRPCLink<AppRouter>[] {
  return [
    supportConciergeLoggingLink,
    httpLink({
      url,
      headers: ({ op }) => {
        const id = traceIdSchema.safeParse(
          op.context.supportCorrelationId,
        ).data;
        return id ? { 'X-Correlation-ID': id } : {};
      },
    }),
  ];
}
